package com.iregained.site.components.sections.corporate.sections

import androidx.compose.runtime.*
import com.iregained.site.theme.toSitePalette
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Staggered Row Slide
@Composable
private fun Modifier.staggeredRowSlide(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else (-20).px).transition(
        Transition.of("opacity", 0.5.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.5.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun CookieMatrixSection(data: CookieMatrixSectionData = defaultCookieMatrixData) {
    val palette = ColorMode.current.toSitePalette()
    var isVisible by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier.fillMaxWidth().backgroundColor(palette.custom.lightGray)
            .padding(topBottom = 20.px, leftRight = 5.percent), contentAlignment = Alignment.Center
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {
            Column(modifier = Modifier.fillMaxWidth().maxWidth(1000.px)) {
                SpanText(
                    text = data.title,
                    modifier = Modifier.fontSize(22.px).color(palette.brand.primary).fontWeight(FontWeight.Bold)
                        .opacity(if (isVisible) 1 else 0).transition(Transition.of("opacity", 0.8.s))
                )

                Column(
                    modifier = Modifier.fillMaxWidth().border(1.px, LineStyle.Solid, palette.custom.divider)
                        .borderRadius(8.px).margin(top = 16.px)
                ) {
                    // Header Row
                    Row(
                        modifier = Modifier.fillMaxWidth().backgroundColor(palette.brand.primarySoft).padding(12.px)
                            .staggeredRowSlide(isVisible, 100)
                    ) {
                        SpanText(
                            "Category",
                            modifier = Modifier.weight(2f).fontWeight(FontWeight.Bold).color(palette.brand.primary)
                        )
                        SpanText(
                            "Purpose",
                            modifier = Modifier.weight(3f).fontWeight(FontWeight.Bold).color(palette.brand.primary)
                                .textAlign(TextAlign.Center)
                        )
                        SpanText(
                            "Necessity",
                            modifier = Modifier.weight(1f).fontWeight(FontWeight.Bold).color(palette.brand.primary)
                                .textAlign(TextAlign.End)
                        )
                    }

                    // Data Rows
                    data.rows.forEachIndexed { index, row ->
                        val rowBg = if (index % 2 == 0) Colors.White else palette.custom.lightGray
                        key(row.category) {
                            Row(
                                modifier = Modifier.fillMaxWidth().backgroundColor(rowBg).padding(12.px).gap(25.px)
                                    .borderBottom(1.px, LineStyle.Solid, palette.custom.divider)
                                    .staggeredRowSlide(isVisible, 200 + (index * 100)),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Div(
                                    attrs = Modifier.fillMaxWidth().weight(1.5f).textAlign(TextAlign.Start)
                                        .fontWeight(FontWeight.SemiBold).color(palette.custom.darkText).toAttrs {
                                            ref { htmlElement ->
                                                htmlElement.innerHTML = row.category
                                                onDispose {}
                                            }
                                        })
                                Div(
                                    attrs = Modifier.fillMaxWidth().weight(3f).textAlign(TextAlign.Start)
                                        .fontSize(14.px).color(palette.custom.darkText).lineHeight(1.4).toAttrs {
                                            ref { htmlElement ->
                                                htmlElement.innerHTML = row.purpose
                                                onDispose {}
                                            }
                                        })
                                Div(
                                    attrs = Modifier.fillMaxWidth().weight(1f).textAlign(TextAlign.End).fontSize(13.px)
                                        .fontWeight(FontWeight.Bold).color(palette.brand.primary).toAttrs {
                                            ref { htmlElement ->
                                                htmlElement.innerHTML = row.necessity
                                                onDispose {}
                                            }
                                        })
                            }
                        }
                    }
                }
            }
        }
    }
}

data class CookieRowData(val category: String, val purpose: String, val necessity: String)
data class CookieMatrixSectionData(val title: String, val rows: List<CookieRowData>)

val defaultCookieMatrixData = CookieMatrixSectionData(
    title = "Types of Cookies We Use", rows = listOf(
        CookieRowData(
            "Strictly Necessary",
            "Required for security, and core functionality of the MyHand<sup>®</sup> resources or access to customer/distributor/sales representative’s/ member only resources.",
            "Mandatory"
        ), CookieRowData(
            "Functional",
            "Remembers user preferences (e.g., language) and enables video rehabilitation content.",
            "Optional"
        ), CookieRowData(
            "Performance & Analytics",
            "Anonymized tracking to improve site speed and content relevance for stroke survivors.",
            "Optional"
        ), CookieRowData(
            "Marketing & Social",
            "Used to provide information about our product via social platforms. Note: These are never used on pages containing patient clinical data.",
            "Optional (Browser Managed)"
        )
    )
)